/**
 * DokumenteVersandPanel.tsx — Slide-over Panel für Dokumentenversand
 * 
 * Öffnet sich rechts am Bildschirmrand.
 * Erlaubt: Beteiligte auswählen, E-Mail schreiben, Dokumente als Anhänge versenden.
 * Nach Versand: Protokoll-Eintrag in aufgaben-Tabelle.
 */
import { useState, useEffect, useCallback } from 'react';
import { X, Send, Loader2, Mail, Users, FileText, CheckCircle2, AlertCircle, Package } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface Dokument {
  id: string;
  dateiname: string;
  dateipfad: string;
  mime_type: string;
  dateigroesse: number;
  titel?: string;
  kategorie?: string;
}

interface Beteiligter {
  id: string;
  name: string | null;
  email: string | null;
  rolle: string;
}

interface DokumenteVersandPanelProps {
  open: boolean;
  onClose: () => void;
  dokumente: Dokument[];
  akteId: string;
  aktenNr: string;
  betriebId?: string;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function DokumenteVersandPanel({ open, onClose, dokumente, akteId, aktenNr, betriebId }: DokumenteVersandPanelProps) {
  const { session, profile } = useAuth();
  const [beteiligte, setBeteiligte] = useState<Beteiligter[]>([]);
  const [selectedBeteiligte, setSelectedBeteiligte] = useState<Set<string>>(new Set());
  const [customEmail, setCustomEmail] = useState('');
  const [betreff, setBetreff] = useState('');
  const [nachricht, setNachricht] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [loadingBeteiligte, setLoadingBeteiligte] = useState(true);
  const [zipMode, setZipMode] = useState(false);

  // Load Beteiligte for this Akte
  useEffect(() => {
    if (!open || !akteId) return;
    let cancelled = false;
    (async () => {
      setLoadingBeteiligte(true);
      const { data } = await supabase
        .from('akten_beteiligte')
        .select('id, name, email, rolle')
        .eq('akte_id', akteId)
        .order('created_at', { ascending: true });
      if (!cancelled) {
        setBeteiligte((data || []).filter((b: any) => b.email));
        setLoadingBeteiligte(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, akteId]);

  // Set default subject
  useEffect(() => {
    if (open && !betreff) {
      setBetreff(`Dokumente zu Akte ${aktenNr}`);
    }
  }, [open, aktenNr]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setSent(false);
      setSelectedBeteiligte(new Set());
      setCustomEmail('');
      setNachricht('');
      setBetreff('');
    }
  }, [open]);

  const toggleBeteiligter = (id: string) => {
    setSelectedBeteiligte(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalSize = dokumente.reduce((s, d) => s + (d.dateigroesse || 0), 0);
  const tooLarge = totalSize > 20 * 1024 * 1024; // 20MB Gmail limit

  const getRecipients = (): string[] => {
    const emails: string[] = [];
    beteiligte.filter(b => selectedBeteiligte.has(b.id) && b.email).forEach(b => emails.push(b.email!));
    if (customEmail.trim()) {
      customEmail.split(',').map(e => e.trim()).filter(e => e.includes('@')).forEach(e => emails.push(e));
    }
    return [...new Set(emails)];
  };

  const handleSend = async () => {
    const recipients = getRecipients();
    if (recipients.length === 0) {
      toast.error('Bitte mindestens einen Empfänger auswählen');
      return;
    }
    if (dokumente.length === 0) {
      toast.error('Keine Dokumente ausgewählt');
      return;
    }

    setSending(true);
    try {
      // 1. Download all documents from Supabase Storage and convert to base64
      const attachments: { filename: string; mimeType: string; base64Data: string }[] = [];
      
      for (const doc of dokumente) {
        const { data, error } = await supabase.storage
          .from('dokumente')
          .download(doc.dateipfad);
        if (error || !data) {
          console.error('Failed to download:', doc.dateiname, error);
          continue;
        }
        const arrayBuffer = await data.arrayBuffer();
        const uint8 = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < uint8.length; i++) {
          binary += String.fromCharCode(uint8[i]);
        }
        const base64 = btoa(binary);
        attachments.push({
          filename: doc.dateiname,
          mimeType: doc.mime_type || 'application/octet-stream',
          base64Data: base64,
        });
      }

      if (attachments.length === 0) {
        toast.error('Keine Dokumente konnten geladen werden');
        setSending(false);
        return;
      }

      // 2. Build raw MIME email
      const senderName = profile?.vorname && profile?.nachname 
        ? `${profile.vorname} ${profile.nachname}` 
        : 'Gutachter Koop';
      const fromEmail = betriebId === 'autoglas' ? 'info@lenneglas.de' : 
                        betriebId === 'mietwagen' ? 'info@mietgo.de' : 
                        'info@gutachter-koop.de';
      
      const htmlBody = `
        <div style="font-family: Arial, sans-serif; font-size: 14px; color: #333;">
          ${nachricht ? `<p>${nachricht.replace(/\n/g, '<br>')}</p><br>` : ''}
          <p>Anbei erhalten Sie ${attachments.length} Dokument${attachments.length > 1 ? 'e' : ''} zur Akte <strong>${aktenNr}</strong>.</p>
          <br>
          <p>Mit freundlichen Grüßen<br>${senderName}</p>
        </div>
      `;

      const boundary = '----=_Part_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2);
      let email = '';
      email += `From: ${senderName} <${fromEmail}>\r\n`;
      email += `To: ${recipients.join(', ')}\r\n`;
      email += `Subject: =?UTF-8?B?${btoa(unescape(encodeURIComponent(betreff)))}?=\r\n`;
      email += `MIME-Version: 1.0\r\n`;
      email += `Content-Type: multipart/mixed; boundary="${boundary}"\r\n`;
      email += `\r\n`;
      // HTML body part
      email += `--${boundary}\r\n`;
      email += `Content-Type: text/html; charset=UTF-8\r\n`;
      email += `Content-Transfer-Encoding: base64\r\n`;
      email += `\r\n`;
      email += btoa(unescape(encodeURIComponent(htmlBody)));
      email += `\r\n`;
      // Attachments
      for (const att of attachments) {
        email += `--${boundary}\r\n`;
        email += `Content-Type: ${att.mimeType}; name="${att.filename}"\r\n`;
        email += `Content-Disposition: attachment; filename="${att.filename}"\r\n`;
        email += `Content-Transfer-Encoding: base64\r\n`;
        email += `\r\n`;
        email += att.base64Data;
        email += `\r\n`;
      }
      email += `--${boundary}--\r\n`;

      const raw = btoa(unescape(encodeURIComponent(email)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      // 3. Send via Gmail API
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('gmail-api', {
        body: { action: 'sendMessage', raw, fromEmail },
      });

      if (sendError) throw new Error(sendError.message || 'Senden fehlgeschlagen');
      if (sendResult?.error) throw new Error(sendResult.error);

      // 4. Log protocol entry
      await supabase.from('aufgaben').insert({
        akte_id: akteId,
        titel: `Dokumente versendet: ${attachments.map(a => a.filename).join(', ')}`,
        beschreibung: `An: ${recipients.join(', ')}\nBetreff: ${betreff}\n${attachments.length} Dokument(e) versendet`,
        status: 'erledigt',
        erstellt_von: session?.user?.id || null,
      });

      setSent(true);
      toast.success(`${attachments.length} Dokument(e) erfolgreich versendet`, {
        description: `An: ${recipients.join(', ')}`,
      });
    } catch (err: any) {
      console.error('Send error:', err);
      toast.error('Versand fehlgeschlagen', { description: err.message });
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px]" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative w-full max-w-[480px] bg-white shadow-2xl border-l border-gray-200 flex flex-col animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center">
              <Send className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-gray-900">Dokumente versenden</h2>
              <p className="text-[11px] text-gray-500">{dokumente.length} Dokument{dokumente.length !== 1 ? 'e' : ''} ausgewählt</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {sent ? (
          /* Success State */
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Erfolgreich versendet</h3>
            <p className="text-[13px] text-gray-500 mb-6">
              {dokumente.length} Dokument{dokumente.length !== 1 ? 'e' : ''} wurden per E-Mail versendet und im Protokoll vermerkt.
            </p>
            <button onClick={onClose} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-[13px] font-medium hover:bg-gray-800 transition-colors">
              Schließen
            </button>
          </div>
        ) : (
          <>
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Selected Documents */}
              <div>
                <div className="flex items-center gap-2 mb-2.5">
                  <FileText className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Dokumente</span>
                  <span className="text-[11px] text-gray-400">({formatBytes(totalSize)})</span>
                </div>
                {tooLarge && (
                  <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-2.5">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                    <p className="text-[11px] text-amber-700">Gesamtgröße überschreitet 20 MB — einige Anhänge könnten abgelehnt werden.</p>
                  </div>
                )}
                <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
                  {dokumente.map(d => (
                    <div key={d.id} className="flex items-center gap-2.5 px-3 py-2 bg-gray-50 rounded-lg">
                      <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-[12px] text-gray-700 truncate flex-1">{d.titel || d.dateiname}</span>
                      <span className="text-[11px] text-gray-400 flex-shrink-0">{formatBytes(d.dateigroesse)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recipients - Beteiligte */}
              <div>
                <div className="flex items-center gap-2 mb-2.5">
                  <Users className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Empfänger</span>
                </div>
                {loadingBeteiligte ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                ) : beteiligte.length > 0 ? (
                  <div className="space-y-1.5 mb-3">
                    {beteiligte.map(b => (
                      <label key={b.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-100 hover:border-gray-200 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedBeteiligte.has(b.id)}
                          onChange={() => toggleBeteiligter(b.id)}
                          className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-gray-900 truncate">{b.name || 'Unbekannt'}</p>
                          <p className="text-[11px] text-gray-500 truncate">{b.rolle} · {b.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-[12px] text-gray-400 italic mb-3">Keine Beteiligten mit E-Mail hinterlegt.</p>
                )}
                
                {/* Custom email input */}
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="email"
                    value={customEmail}
                    onChange={e => setCustomEmail(e.target.value)}
                    placeholder="Weitere E-Mail-Adresse(n), kommagetrennt..."
                    className="w-full pl-9 pr-3 py-2.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 placeholder:text-gray-400"
                  />
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Betreff</label>
                <input
                  type="text"
                  value={betreff}
                  onChange={e => setBetreff(e.target.value)}
                  className="w-full px-3 py-2.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                />
              </div>

              {/* Message */}
              <div>
                <label className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Nachricht (optional)</label>
                <textarea
                  value={nachricht}
                  onChange={e => setNachricht(e.target.value)}
                  rows={4}
                  placeholder="Optionale Nachricht an den Empfänger..."
                  className="w-full px-3 py-2.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 placeholder:text-gray-400 resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 space-y-3">
              <div className="flex items-center justify-between text-[11px] text-gray-500">
                <span>Von: {betriebId === 'autoglas' ? 'info@lenneglas.de' : betriebId === 'mietwagen' ? 'info@mietgo.de' : 'info@gutachter-koop.de'}</span>
                <span>{getRecipients().length} Empfänger</span>
              </div>
              <button
                onClick={handleSend}
                disabled={sending || getRecipients().length === 0}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 text-white rounded-lg text-[13px] font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {dokumente.length} Dokument{dokumente.length !== 1 ? 'e' : ''} versenden
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
